import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { buildBreakdownSegments, headlineTotal } from './invoiceTotalMeta';

type InvoiceTotalProps = {
    /** Ancillary charges — freight, customs, … */
    expensesTotalTetri: number;
    /** Σ `purchasePrice × quantity` across the invoice's lines. */
    linesTotalTetri: number;
    /** The row's id — the dynamic discriminator on every testid here. */
    rowId: string;
    /** Literal, static testid prefix for the owning table's row. */
    slot: string;
    /** `linesTotal + expensesTotal`, as the server computed it. */
    totalAmountTetri: number;
    /** VAT the supplier charged. Omitted on drafts and pending invoices. */
    vatTotalTetri?: number;
    /** `linesTotal + vatTotal`, as the server computed it. */
    grossTotalTetri?: number;
    /** The part of `expensesTotal` the supplier billed on their own invoice. */
    expensesOnInvoiceTotalTetri?: number;
    /**
     * `grossTotal + expensesOnInvoiceTotal` — what the supplier is owed, and
     * the headline when it is known. Omitted on drafts and pending invoices.
     */
    payableTotalTetri?: number;
};

/**
 * An invoice total with its composition underneath when there is one to show.
 *
 * The headline number is what the supplier's document says: the lines including
 * the VAT they charged, plus the charges they billed themselves. It used to be
 * the landed ex-VAT cost, which meant one row carried three unlabelled bases at
 * once — a 240.00 total, a 236.00 outstanding beside it, and a 276.00 journal
 * entry for the same invoice (BOS-161). Now the headline is the one an
 * accountant can match against the paper, and the line underneath spells out
 * the VAT, the ancillary charges, and what the whole thing booked.
 *
 * Shared by all three purchases tables so one number is never spelled two ways
 * on the same page. `slot` stays a literal prefix per the testid convention;
 * the row id goes on the end.
 */
export const InvoiceTotal: React.FC<InvoiceTotalProps> = ({
    expensesTotalTetri,
    linesTotalTetri,
    rowId,
    slot,
    totalAmountTetri,
    vatTotalTetri,
    grossTotalTetri,
    expensesOnInvoiceTotalTetri,
    payableTotalTetri,
}) => {
    const { t } = useTranslation('pages');

    const segments = buildBreakdownSegments({
        linesTotalTetri,
        expensesTotalTetri,
        vatTotalTetri,
        grossTotalTetri,
        expensesOnInvoiceTotalTetri,
    });

    return (
        <div className="flex flex-col items-end gap-0.5">
            <span
                className="text-sm font-bold tabular-nums text-foreground"
                {...getNodeTestIdProp(`${slot}-total-${rowId}`)}
            >
                {formatGel(
                    headlineTotal({ totalAmountTetri, payableTotalTetri }),
                )}
            </span>
            {segments.length > 0 && (
                <span
                    className="text-[11px] tabular-nums text-muted"
                    {...getNodeTestIdProp(`${slot}-total-breakdown-${rowId}`)}
                >
                    {segments
                        .map((segment) => t(segment.key, segment.params))
                        .join(' · ')}
                </span>
            )}
        </div>
    );
};
